import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { recursiveSnakeCaseCipher } from "@src/global_functions/postgrestApi";
import { downloadFileByUrl } from "@src/global_functions/util";
import { useQueryClient } from "@tanstack/react-query";
import { VITE_UPLOAD_BASE_URL } from "../../../env";
import request from "../../request";
import FetchClients from "../client";
import { UTILITY_BILL_CONSUMPTION_KEY } from "../utilities/consumption";

const QUERY_KEY = "uploadable-bill";

const backpackBill = {
  useQuery,
  download,
  mutations: {
    usePost,
  },
} as const;

export default backpackBill;

// GET (S3 download url)

// endpoint requires {feature: "Utilities", access: "read"}
// Returns url to download bill
async function getDownloadBackpackBill(
  site_id: number,
  bill_id: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.d3.GET(`/backpack_bill/download`, {
    params: {
      query: {
        site_id,
        bill_id,
      },
    },
    headers: checkFeatureAccessHeader,
    parseAs: "text",
  });
  if (data) return data;
  throw new Error(error);
}

// endpoint requires {feature: "Utilities", access: "read"}
async function getDownloadBackpackBills(
  site_id: number,
  bill_ids: number[],
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.d3.GET(
    `/backpack_bill/download_zip`,
    {
      params: {
        query: {
          site_id,
          bill_ids,
        },
      },
      headers: checkFeatureAccessHeader,
      parseAs: "text",
    }
  );
  if (data) return data;
  throw new Error(error);
}

function useQuery(siteId: number, billId?: number, enabled = true) {
  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getDownloadBackpackBill(siteId, billId!, checkFeatureAccessHeader),
    {
      queryKey: [QUERY_KEY, siteId, billId],
      enabled: billId !== undefined && enabled,
      staleTime: Infinity,
    }
  );
}

// POST (upload file)

interface BackpackBillUploadRequest {
  filename: string;
  siteId: number;
  billId: number;
  file: File;
}

// endpoint requires {feature: "Utilities", access: "edit"}
const postUploadBackpackBill = (
  { file, ...req }: BackpackBillUploadRequest,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
): Promise<void> =>
  request
    .post(`${VITE_UPLOAD_BASE_URL}/backpack_bill/upload`)
    .set(checkFeatureAccessHeader)
    .query(recursiveSnakeCaseCipher(req))
    .send(file)
    .then(() => {});

function usePost() {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    (req: BackpackBillUploadRequest, checkFeatureAccessHeader) =>
      postUploadBackpackBill(req, checkFeatureAccessHeader),
    {
      onSuccess: async (_, { siteId, billId }) => {
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEY, siteId, billId],
        });
        // BE also updates utility bill entity
        await queryClient.invalidateQueries({
          queryKey: [UTILITY_BILL_CONSUMPTION_KEY, siteId],
        });
      },
    }
  );
}

/**
 * Downloads one bill, or a zip when several are requested.
 *
 * Not a hook, so callers must supply the header from
 * `usePreRequestFeatureAccessCheck("Utilities", "read")` themselves.
 */
async function download(
  siteId: number,
  billIds: number[],
  checkFeatureAccessHeader: CheckFeatureAccessHeader = {}
) {
  if (billIds.length === 0) return;
  const url =
    billIds.length === 1
      ? await getDownloadBackpackBill(
          siteId,
          billIds[0]!,
          checkFeatureAccessHeader
        )
      : await getDownloadBackpackBills(
          siteId,
          billIds,
          checkFeatureAccessHeader
        );

  if (url) {
    downloadFileByUrl(url);
  }
}
